/**
 * CorrectionDetail — one Earnings Correction: the live check against today's
 * ChannelMeter data (drift, rows that no longer exist, overlaps with applied
 * corrections), its paid → corrected lines, provenance, settlement and
 * history, plus the draft/apply/revert actions for `financials.corrections`.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Pill, Spinner, StatusPill, tableStyles } from '../../ui';
import type { ToastKind } from '../../ui';
import {
  discardEarningsCorrection,
  getEarningsCorrection,
  updateEarningsCorrection,
  updateEarningsCorrectionSettlement,
  CorrectionLiveView,
  EarningsCorrection,
  SettlementStatus,
} from '../../../services/earningsCorrectionsApi';
import {
  BUCKET_LABEL,
  correctionErrorMessage,
  formatCount,
  formatSignedCents,
  formatUsd,
  KIND_LABEL,
  lineDeltaCents,
  monthLabel,
  monthRangeLabel,
  SETTLEMENT_META,
  sourceFlagMeta,
  STATUS_META,
} from './correctionModel';
import { cs, deltaColor, noticeStyle } from './correctionStyles';
import ApplyCorrectionDialog, { RevertCorrectionDialog } from './ApplyCorrectionDialog';

interface CorrectionDetailProps {
  id: string;
  canCorrect: boolean;
  onBack: () => void;
  /** Open another correction (supersede links). */
  onOpen: (id: string) => void;
  onNotify: (toast: { kind: ToastKind; title: string; message?: string }) => void;
}

const fmtWhen = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

const LINES_FILTER_THRESHOLD = 25;

/** A draft that the server would refuse to apply right now, and why. */
export function applyBlockers(correction: EarningsCorrection, live: CorrectionLiveView): string[] {
  if (correction.status !== 'draft') return [];
  const out: string[] = [];
  if (live.missingMonths.length) out.push(`ChannelMeter no longer has ${monthRangeLabel(live.missingMonths)} stored.`);
  if (live.unresolved.length) out.push(`${live.unresolved.length} line(s) no longer match a ChannelMeter row.`);
  if (live.drifted.length) out.push(`ChannelMeter changed ${live.drifted.length} row(s) since this draft was saved.`);
  return out;
}

const CorrectionDetail: React.FC<CorrectionDetailProps> = ({ id, canCorrect, onBack, onOpen, onNotify }) => {
  const [correction, setCorrection] = useState<EarningsCorrection | null>(null);
  const [live, setLive] = useState<CorrectionLiveView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);
  const [lineQuery, setLineQuery] = useState('');
  const [settlementStatus, setSettlementStatus] = useState<SettlementStatus>('pending');
  const [settlementNote, setSettlementNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getEarningsCorrection(id);
      setCorrection(res.correction);
      setLive(res.live);
      setSettlementStatus(res.correction.settlement.status);
      setSettlementNote(res.correction.settlement.note ?? '');
    } catch (err) {
      setError(correctionErrorMessage(err, 'Failed to load the correction.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(correctionErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const refreshBases = () =>
    run('refresh', async () => {
      // A save re-measures every base against today's ChannelMeter data.
      await updateEarningsCorrection(id, {});
      await load();
      onNotify({ kind: 'success', title: 'Draft refreshed', message: 'Every line was re-measured against today’s ChannelMeter data.' });
    });

  const discard = () => {
    if (!correction) return;
    if (!window.confirm(`Discard the draft “${correction.title}”? It was never applied, so no figure changes.`)) return;
    run('discard', async () => {
      await discardEarningsCorrection(id);
      await load();
      onNotify({ kind: 'info', title: 'Draft discarded' });
    });
  };

  const saveSettlement = () =>
    run('settlement', async () => {
      const res = await updateEarningsCorrectionSettlement(id, { status: settlementStatus, note: settlementNote.trim() || null });
      setCorrection(res.correction);
      onNotify({ kind: 'success', title: 'Settlement recorded', message: SETTLEMENT_META[res.correction.settlement.status].label });
    });

  const lines = useMemo(() => {
    if (!correction) return [];
    const q = lineQuery.trim().toLowerCase();
    if (!q) return correction.lines;
    return correction.lines.filter(
      (l) =>
        l.channelName.toLowerCase().includes(q) ||
        l.channelId.toLowerCase() === q ||
        l.userName.toLowerCase().includes(q) ||
        l.month.includes(q),
    );
  }, [correction, lineQuery]);

  if (loading && !correction) {
    return (
      <div style={cs.section}>
        <div style={cs.loadingRow}>
          <Spinner label="Loading correction…" />
        </div>
      </div>
    );
  }

  if (!correction || !live) {
    return (
      <div style={cs.section}>
        <button type="button" style={cs.linkButton} onClick={onBack}>
          ← All corrections
        </button>
        <div style={{ ...noticeStyle('danger'), marginTop: 12 }} role="alert">
          {error ?? 'Correction not found.'}
        </div>
      </div>
    );
  }

  const net = correction.summary.netDeltaCents;
  const liveNet = live.netDeltaCents;
  const blockers = applyBlockers(correction, live);
  const isDraft = correction.status === 'draft';
  const settleable = correction.status === 'draft' || correction.status === 'applied';
  const settlementDirty =
    settlementStatus !== correction.settlement.status || (settlementNote.trim() || null) !== (correction.settlement.note ?? null);

  return (
    <div style={cs.stack}>
      <div>
        <button type="button" style={cs.linkButton} onClick={onBack}>
          ← All corrections
        </button>
      </div>

      {/* ---- Header ---- */}
      <div style={cs.section}>
        <div style={cs.header}>
          <div>
            <div style={cs.row}>
              <h2 style={cs.title}>{correction.title}</h2>
              <StatusPill variant={STATUS_META[correction.status].variant}>{STATUS_META[correction.status].label}</StatusPill>
              {settleable && (
                <StatusPill variant={SETTLEMENT_META[correction.settlement.status].variant} noDot>
                  {SETTLEMENT_META[correction.settlement.status].label}
                </StatusPill>
              )}
            </div>
            <p style={cs.subtitle}>
              {KIND_LABEL[correction.kind]} · {monthRangeLabel(correction.months)} · {correction.summary.lineCount} line
              {correction.summary.lineCount === 1 ? '' : 's'} · {correction.channelIds.length} channel
              {correction.channelIds.length === 1 ? '' : 's'} — {STATUS_META[correction.status].description}
            </p>
          </div>
          {canCorrect && (
            <div style={cs.controls}>
              {isDraft && (
                <>
                  <Button variant="subtle" size="sm" onClick={refreshBases} disabled={busy !== null}>
                    {busy === 'refresh' ? 'Refreshing…' : 'Refresh from ChannelMeter'}
                  </Button>
                  <Button variant="destructive" size="sm" onClick={discard} disabled={busy !== null}>
                    Discard draft
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setApplyOpen(true)}
                    disabled={busy !== null || blockers.length > 0}
                    title={blockers.length ? blockers.join(' ') : 'Review the impact and apply'}
                  >
                    Review &amp; apply
                  </Button>
                </>
              )}
              {correction.status === 'applied' && (
                <Button variant="destructive" size="sm" onClick={() => setRevertOpen(true)} disabled={busy !== null}>
                  Revert…
                </Button>
              )}
            </div>
          )}
        </div>

        <div style={cs.kvGrid}>
          <div>
            <div style={cs.kvLabel}>Net change (saved)</div>
            <div style={{ ...cs.kvValue, fontSize: 20, fontWeight: 700, color: deltaColor(net.total) }}>{formatSignedCents(net.total)}</div>
            <div style={cs.small}>
              Direct Sold {formatSignedCents(net.directSold)} · AdSense {formatSignedCents(net.adSense)}
            </div>
          </div>
          {isDraft && (
            <div>
              <div style={cs.kvLabel}>Net change vs ChannelMeter now</div>
              <div style={{ ...cs.kvValue, fontSize: 20, fontWeight: 700, color: deltaColor(liveNet.total) }}>
                {formatSignedCents(liveNet.total)}
              </div>
              <div style={cs.small}>What “Review &amp; apply” asks you to confirm.</div>
            </div>
          )}
          <div>
            <div style={cs.kvLabel}>Created</div>
            <div style={cs.kvValue}>{fmtWhen(correction.createdAt)}</div>
            <div style={cs.small}>{correction.createdBy.email}</div>
          </div>
          {correction.appliedAt && (
            <div>
              <div style={cs.kvLabel}>Applied</div>
              <div style={cs.kvValue}>{fmtWhen(correction.appliedAt)}</div>
              <div style={cs.small}>{correction.appliedBy?.email}</div>
            </div>
          )}
          {correction.revertedAt && (
            <div>
              <div style={cs.kvLabel}>Reverted</div>
              <div style={cs.kvValue}>{fmtWhen(correction.revertedAt)}</div>
              <div style={cs.small}>{correction.revertedBy?.email}</div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div style={noticeStyle('danger')} role="alert">
          {error}
        </div>
      )}

      {/* ---- Live checks ---- */}
      {isDraft && live.missingMonths.length > 0 && (
        <div style={noticeStyle('danger')}>
          ChannelMeter no longer has {monthRangeLabel(live.missingMonths)} stored, so this draft can’t be applied. Discard it.
        </div>
      )}
      {isDraft && live.unresolved.length > 0 && (
        <div style={noticeStyle('danger')}>
          <strong>{live.unresolved.length} line(s) no longer match a ChannelMeter row.</strong> The payee or channel left
          that month. Discard this draft and start a new one.
          <ul style={cs.list}>
            {live.unresolved.slice(0, 8).map((u) => (
              <li key={u.rowKeyId}>
                {monthLabel(u.month)} · {u.userName} · {u.channelName} · {u.revenueSource}
              </li>
            ))}
          </ul>
        </div>
      )}
      {isDraft && live.drifted.length > 0 && (
        <div style={noticeStyle('warning')}>
          <strong>ChannelMeter changed {live.drifted.length} row(s) since this draft was saved.</strong> Refresh from
          ChannelMeter to re-measure every line, then check the net change again before applying.
          <ul style={cs.list}>
            {live.drifted.slice(0, 8).map((d) => (
              <li key={d.rowKeyId}>
                {monthLabel(d.month)} · {d.channelName} · {d.revenueSource}: earnings {formatUsd(d.base.earningsUsd)} →{' '}
                {formatUsd(d.now.earningsUsd)} now
              </li>
            ))}
          </ul>
        </div>
      )}
      {isDraft && live.overlaps.length > 0 && (
        <div style={noticeStyle('info')}>
          Some rows are already covered by an applied correction. Applying this one supersedes it, and{' '}
          <strong>all</strong> of the superseded correction’s lines stop applying:
          <ul style={cs.list}>
            {live.overlaps.map((o) => (
              <li key={o.correctionId}>
                {o.rows} row(s) in{' '}
                <button type="button" style={cs.linkButton} onClick={() => onOpen(o.correctionId)}>
                  correction {o.correctionId}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {correction.status === 'applied' && live.notOwned > 0 && (
        <div style={noticeStyle('warning')}>
          {live.notOwned} of this correction’s rows now belong to a later correction.
        </div>
      )}
      {correction.supersededBy && (
        <div style={noticeStyle('neutral')}>
          Superseded by{' '}
          <button type="button" style={cs.linkButton} onClick={() => onOpen(correction.supersededBy!)}>
            correction {correction.supersededBy}
          </button>
          . Its lines no longer apply.
        </div>
      )}
      {correction.supersedes.length > 0 && (
        <div style={noticeStyle('neutral')}>
          Superseded{' '}
          {correction.supersedes.map((sid, i) => (
            <React.Fragment key={sid}>
              {i > 0 && ', '}
              <button type="button" style={cs.linkButton} onClick={() => onOpen(sid)}>
                correction {sid}
              </button>
            </React.Fragment>
          ))}{' '}
          when it was applied.
        </div>
      )}
      {correction.revertReason && (
        <div style={noticeStyle('neutral')}>
          <strong>Revert reason:</strong> {correction.revertReason}
        </div>
      )}

      {/* ---- Reason, client note, settlement ---- */}
      <div style={cs.section}>
        <div style={cs.kvGrid}>
          <div>
            <div style={cs.kvLabel}>Reason (internal)</div>
            <div style={{ ...cs.kvValue, whiteSpace: 'pre-wrap' }}>{correction.reason}</div>
          </div>
          <div>
            <div style={cs.kvLabel}>Client note</div>
            <div style={cs.kvValue}>
              {correction.clientNote ?? <span style={cs.muted}>None: client pages use the standard “Updated” wording.</span>}
            </div>
            <div style={cs.help}>The only text from a correction that reaches a client report page.</div>
          </div>
        </div>

        {settleable && (
          <div style={{ marginTop: 16 }}>
            <div style={cs.subheading}>Settlement (outside ChannelMeter)</div>
            {canCorrect ? (
              <div style={cs.row}>
                <select
                  aria-label="Settlement status"
                  style={cs.select}
                  value={settlementStatus}
                  onChange={(e) => setSettlementStatus(e.target.value as SettlementStatus)}
                >
                  <option value="pending">Pending</option>
                  <option value="settled">Settled</option>
                  <option value="not_required">No settlement needed</option>
                </select>
                <input
                  aria-label="Settlement note"
                  style={{ ...cs.input, width: 360, maxWidth: '100%' }}
                  placeholder="e.g. Paid by wire on Oct 3 · invoice #"
                  maxLength={500}
                  value={settlementNote}
                  onChange={(e) => setSettlementNote(e.target.value)}
                />
                <Button variant="subtle" size="sm" onClick={saveSettlement} disabled={busy !== null || !settlementDirty}>
                  {busy === 'settlement' ? 'Saving…' : 'Save settlement'}
                </Button>
              </div>
            ) : (
              <div style={cs.small}>{correction.settlement.note ?? SETTLEMENT_META[correction.settlement.status].label}</div>
            )}
            {correction.settlement.updatedAt && (
              <div style={cs.help}>
                Last updated {fmtWhen(correction.settlement.updatedAt)} by {correction.settlement.updatedBy}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ---- Sources (DS reprocess) ---- */}
      {correction.sources.length > 0 && (
        <div style={cs.section}>
          <div style={cs.subheading}>Where the new figures came from</div>
          <div style={tableStyles.wrapScroll}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th style={tableStyles.th}>Month</th>
                  <th style={tableStyles.th}>GAM upload</th>
                  <th style={tableStyles.th}>Replaced upload from</th>
                  <th style={tableStyles.th}>Rate card updated</th>
                  <th style={tableStyles.th}>Flags</th>
                </tr>
              </thead>
              <tbody>
                {correction.sources.map((s) => (
                  <tr key={s.month}>
                    <td style={tableStyles.td}>{monthLabel(s.month)}</td>
                    <td style={tableStyles.td}>
                      <div>{s.uploadFileName ?? '—'}</div>
                      <div style={cs.small}>
                        {fmtWhen(s.uploadedAt)}
                        {s.uploadedBy ? ` · ${s.uploadedBy}` : ''}
                      </div>
                    </td>
                    <td style={tableStyles.td}>{fmtWhen(s.replacedPreviousAt)}</td>
                    <td style={tableStyles.td}>{fmtWhen(s.rateCardUpdatedAt)}</td>
                    <td style={tableStyles.td}>
                      <div style={cs.row}>
                        {s.flags.length === 0 && <span style={cs.muted}>—</span>}
                        {s.flags.map((f) => {
                          const meta = sourceFlagMeta(f);
                          return (
                            <Pill key={f} tone={meta.tone} title={meta.description}>
                              {meta.label}
                            </Pill>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---- Lines ---- */}
      <div style={cs.section}>
        <div style={{ ...cs.header, marginBottom: 12 }}>
          <div style={cs.subheading}>
            Lines ({correction.lines.length}) · ChannelMeter (paid) → corrected
          </div>
          {correction.lines.length > LINES_FILTER_THRESHOLD && (
            <input
              aria-label="Filter lines"
              style={{ ...cs.input, width: 240 }}
              placeholder="Filter by channel, payee or month…"
              value={lineQuery}
              onChange={(e) => setLineQuery(e.target.value)}
            />
          )}
        </div>
        <div style={tableStyles.wrapScroll}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>Month</th>
                <th style={tableStyles.th}>Channel</th>
                <th style={tableStyles.th}>Payee · source</th>
                <th style={tableStyles.thNum}>Earnings (CM → corrected)</th>
                <th style={tableStyles.thNum}>Change</th>
                <th style={tableStyles.thNum}>Gross (CM → corrected)</th>
                <th style={tableStyles.thNum}>Performance</th>
                <th style={tableStyles.th}>Note</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const delta = lineDeltaCents(l);
                const metric = l.target?.performanceMetric ?? (l.bucket === 'directSold' ? 'impressions' : 'views');
                return (
                  <tr key={`${l.month}|${l.bucket}|${l.userKey}|${l.channelId}|${l.channelName}|${l.revenueSource}`}>
                    <td style={tableStyles.td}>{monthLabel(l.month)}</td>
                    <td style={tableStyles.td}>
                      <div style={{ fontWeight: 500 }}>{l.channelName || '—'}</div>
                      <code style={cs.mono}>{l.channelId}</code>
                    </td>
                    <td style={tableStyles.td}>
                      <div>{l.userName}</div>
                      <div style={cs.small}>
                        {BUCKET_LABEL[l.bucket]} · {l.revenueSource}
                        {l.op === 'remove' ? ' · removed' : l.base.rowCount === 0 ? ' · new row' : ''}
                      </div>
                    </td>
                    <td style={tableStyles.tdNum}>
                      {formatUsd(l.base.earningsUsd)} → <strong>{l.target ? formatUsd(l.target.earningsUsd) : 'removed'}</strong>
                    </td>
                    <td style={{ ...tableStyles.tdNum, fontWeight: 600, color: deltaColor(delta) }}>{formatSignedCents(delta)}</td>
                    <td style={tableStyles.tdNum}>
                      {formatUsd(l.base.grossUsd)} → {l.target ? formatUsd(l.target.grossUsd) : '—'}
                    </td>
                    <td style={tableStyles.tdNum}>
                      {formatCount(l.base.performance)} → {l.target ? formatCount(l.target.performance) : '—'}
                      <div style={cs.small}>{metric}</div>
                    </td>
                    <td style={{ ...tableStyles.td, maxWidth: 220 }}>
                      <span style={cs.small}>{l.note ?? ''}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---- History ---- */}
      <div style={cs.section}>
        <div style={cs.subheading}>History</div>
        <ul style={cs.list}>
          {[...correction.history].reverse().map((h, i) => (
            <li key={`${h.at}-${i}`}>
              <strong style={{ textTransform: 'capitalize' }}>{h.action}</strong> · {fmtWhen(h.at)} · {h.by}
              {h.note ? ` — ${h.note}` : ''}
            </li>
          ))}
        </ul>
      </div>

      {applyOpen && (
        <ApplyCorrectionDialog
          correction={correction}
          live={live}
          onClose={() => setApplyOpen(false)}
          onApplied={async (result) => {
            setApplyOpen(false);
            await load();
            onNotify({
              kind: 'success',
              title: 'Correction applied',
              message:
                `The Earnings tab, chat and client pages now show the corrected figures.` +
                (result.superseded.length ? ` Superseded ${result.superseded.length} earlier correction(s).` : ''),
            });
          }}
          onStale={async () => {
            setApplyOpen(false);
            await load();
          }}
        />
      )}
      {revertOpen && (
        <RevertCorrectionDialog
          correction={correction}
          onClose={() => setRevertOpen(false)}
          onReverted={async () => {
            setRevertOpen(false);
            await load();
            onNotify({ kind: 'info', title: 'Correction reverted', message: 'Every surface shows ChannelMeter’s figures for these rows again.' });
          }}
        />
      )}
    </div>
  );
};

export default CorrectionDetail;
